/**
 * User model — shapes API user payloads with defaults and derived values.
 * Use User.fromApi(apiResponse) when consuming auth/user endpoints.
 *
 * Backend response includes plan_slug (starter|growth|pro) and feature_keys (string[]) for plan-based gating.
 */

/** Feature key -> minimum plan slug required (for upgrade modal). Must match backend PlanSeeder. */
export const FEATURE_REQUIRED_PLAN = {
  announcement: 'growth',
  announcements: 'growth',
  analytics: 'pro',
  table_reservation: 'pro',
  feedback: 'growth',
  mailing_list: 'pro',
  priority_support: 'pro',
  custom_integrations: 'pro',
  vip_site_fields: 'vip',
  /** Owner dashboard: list quick orders (Pro, VIP, and users with vip flag). */
  owner_orders: 'pro',
}

export default class User {
  constructor(data = {}) {
    this._id = data.uuid ?? data.id ?? data._id ?? null
    this._name = data.name ?? ''
    this._email = data.email ?? ''
    this._emailVerifiedAt = data.email_verified_at ?? data.emailVerifiedAt ?? null
    this._pendingEmail = data.pending_email ?? data.pendingEmail ?? null
    this._isPaid = data.is_paid === true
    this._planSlug = data.plan_slug ?? data.planSlug ?? 'starter'
    this._featureKeys = Array.isArray(data.feature_keys) ? data.feature_keys : (data.featureKeys ?? [])
    this._subscriptionEndsAt = data.subscription_ends_at ?? data.subscriptionEndsAt ?? null
    this._isSuperadmin = data.is_superadmin === true
    this._isActive = data.is_active !== false
    this._firstLoginTourCompletedAt = data.first_login_tour_completed_at ?? data.firstLoginTourCompletedAt ?? null
    this._vip = data.vip === true
    this._canChangePassword = data.can_change_password !== false
    this._firstName = data.first_name ?? data.firstName ?? ''
    this._lastName = data.last_name ?? data.lastName ?? ''
    this._avatarUrl = data.avatar_url ?? data.avatarUrl ?? null
    this._restaurants = Array.isArray(data.restaurants) ? data.restaurants : (data.restaurants ?? [])
  }

  get id() {
    return this._id
  }

  /** Public uuid for API calls (same as id; use for PATCH/GET paths). */
  get uuid() {
    return this._id
  }

  get name() {
    return this._name
  }

  get email() {
    return this._email
  }

  get emailVerifiedAt() {
    return this._emailVerifiedAt
  }

  get isEmailVerified() {
    return this._emailVerifiedAt != null
  }

  /** New email awaiting verification (after profile email change). */
  get pendingEmail() {
    return this._pendingEmail
  }

  /** True if user has an active paid subscription (Growth or Pro). */
  get isPaid() {
    return this._isPaid
  }

  /** Plan slug: starter | growth | pro. Used for feature gating and upgrade modal. */
  get planSlug() {
    return this._planSlug
  }

  /** List of feature keys included in the user's plan (from API). Use hasFeature(key) to check. */
  get featureKeys() {
    return this._featureKeys
  }

  /** Whether the user's plan includes the given feature key. */
  hasFeature(featureKey) {
    if (!featureKey || !Array.isArray(this._featureKeys)) return false
    const k = String(featureKey).toLowerCase()
    return this._featureKeys.some((f) => String(f).toLowerCase() === k)
  }

  /** Minimum plan slug required for this feature (for upgrade modal). Returns 'growth' or 'pro' or null. */
  static requiredPlanForFeature(featureKey) {
    return FEATURE_REQUIRED_PLAN[String(featureKey).toLowerCase()] ?? null
  }

  /** ISO 8601 datetime when Pro subscription period ends; null when no subscription. */
  get subscriptionEndsAt() {
    return this._subscriptionEndsAt
  }

  /** Formatted label for display, e.g. "Pro until March 19", or null if no subscription. */
  get proUntilLabel() {
    const raw = this._subscriptionEndsAt
    if (!raw || typeof raw !== 'string') return null
    try {
      const d = new Date(raw)
      if (Number.isNaN(d.getTime())) return null
      return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
    } catch {
      return null
    }
  }

  /** True only for the superadmin user (access to superadmin endpoints). */
  get isSuperadmin() {
    return this._isSuperadmin
  }

  /** False when account has been deactivated by superadmin; default true. */
  get isActive() {
    return this._isActive
  }

  /** ISO 8601 datetime when first-login tour was completed/dismissed; null = show tour. */
  get firstLoginTourCompletedAt() {
    return this._firstLoginTourCompletedAt
  }

  /** True when the user has already completed or dismissed the first-login tour. */
  get hasCompletedFirstLoginTour() {
    return this._firstLoginTourCompletedAt != null
  }

  /** True when user is allowed to log in via the mobile app (VIP). */
  get vip() {
    return this._vip
  }

  /**
   * False when the account was first created via Google/Facebook/Instagram (no local password to change).
   * User may set a password via Forgot password; then the API sets this to true.
   */
  get canChangePassword() {
    return this._canChangePassword
  }

  get firstName() {
    return this._firstName
  }

  get lastName() {
    return this._lastName
  }

  /** Display name: name from API, or email local part, or "Guest". */
  get fullName() {
    if (this._name) return this._name
    if (this._firstName || this._lastName) {
      return [this._firstName, this._lastName].filter(Boolean).join(' ').trim()
    }
    if (this._email) return this._email.split('@')[0]
    return 'Guest'
  }

  get avatarUrl() {
    return this._avatarUrl
  }

  /** List of restaurants owned by this user (superadmin list only). Each item: { uuid, name, slug }. */
  get restaurants() {
    return this._restaurants
  }

  /** Build from API response. Use this when consuming user/auth APIs. */
  static fromApi(data) {
    if (data instanceof User) return data
    return new User(data)
  }

  /** Plain object for store/state or sending back to API if needed. */
  toJSON() {
    return {
      id: this._id,
      uuid: this._id,
      name: this._name,
      email: this._email,
      emailVerifiedAt: this._emailVerifiedAt,
      isEmailVerified: this.isEmailVerified,
      pendingEmail: this._pendingEmail,
      isPaid: this._isPaid,
      planSlug: this._planSlug,
      featureKeys: this._featureKeys,
      subscriptionEndsAt: this._subscriptionEndsAt,
      proUntilLabel: this.proUntilLabel,
      isSuperadmin: this._isSuperadmin,
      isActive: this._isActive,
      vip: this._vip,
      canChangePassword: this._canChangePassword,
      firstLoginTourCompletedAt: this._firstLoginTourCompletedAt,
      hasCompletedFirstLoginTour: this.hasCompletedFirstLoginTour,
      fullName: this.fullName,
      firstName: this._firstName,
      lastName: this._lastName,
      avatarUrl: this._avatarUrl,
    }
  }
}
